import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { TrendingUp, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

const API_URL =
  import.meta.env.VITE_BACKEND_API_URL || "https://api.nordex.trade";

interface Market {
  symbol: string;
  display_name: string | null;
  base_symbol: string;
  quote_symbol: string;
  tick_size: string;
  min_order_value_usd: string;
  is_active: boolean;
  is_featured: boolean;
  sort_order: number;
}

const MARKET_COLORS: Record<string, string> = {
  BTC: "amber",
  ETH: "blue",
  SOL: "purple",
  BNB: "amber",
  XRP: "blue",
  ADA: "blue",
  DOGE: "amber",
  MATIC: "purple",
  DOT: "pink",
  LTC: "slate",
};

function getMarketColor(baseSymbol: string) {
  return MARKET_COLORS[baseSymbol] || "blue";
}

function getColorClasses(color: string) {
  const map: Record<string, { bg: string; text: string }> = {
    amber: { bg: "bg-amber-500/10", text: "text-amber-500" },
    blue: { bg: "bg-blue-500/10", text: "text-blue-500" },
    purple: { bg: "bg-purple-500/10", text: "text-purple-500" },
    pink: { bg: "bg-pink-500/10", text: "text-pink-500" },
    slate: { bg: "bg-slate-500/10", text: "text-slate-500" },
  };
  return map[color] || map.blue;
}

export const Markets = () => {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMarkets() {
      try {
        const res = await fetch(`${API_URL}/api/markets`);
        if (!res.ok) throw new Error("Failed to load markets");
        const data: Market[] = await res.json();
        setMarkets(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load markets"
        );
      } finally {
        setLoading(false);
      }
    }
    fetchMarkets();
  }, []);

  const featuredMarkets = markets.filter((m) => m.is_featured);
  const otherMarkets = markets.filter((m) => !m.is_featured);

  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Available Markets</h1>

        <div className="text-sm text-muted-foreground mb-8">
          <p>
            Explore the perpetual markets available for trading on Nordex
          </p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Market Overview</h2>
          <p className="text-muted-foreground mb-6">
            {loading ? (
              "Loading market data..."
            ) : error ? (
              "Nordex offers a wide range of perpetual futures markets, allowing you to trade the price movements of major cryptocurrencies with leverage. All markets are quoted in USD and settled in USDC."
            ) : (
              <>
                Nordex currently offers{" "}
                <strong className="text-foreground">
                  {markets.length} perpetual futures markets
                </strong>
                , allowing you to trade the price movements of major
                cryptocurrencies with leverage. All markets are quoted in USD
                and settled in USDC.
              </>
            )}
          </p>
        </section>

        {/* Markets List */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            Available Perpetual Markets
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading markets...</span>
            </div>
          ) : error ? (
            <div className="p-6 border border-border rounded-lg bg-card text-center text-sm text-muted-foreground">
              Unable to load market data. Please try again later.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Featured markets with full cards */}
              {featuredMarkets.map((market) => {
                const color = getMarketColor(market.base_symbol);
                const classes = getColorClasses(color);
                const displayName =
                  market.display_name || market.base_symbol;

                return (
                  <div
                    key={market.symbol}
                    className="p-6 border border-border rounded-lg bg-card"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${classes.bg}`}>
                          <TrendingUp
                            className={`h-6 w-6 ${classes.text}`}
                          />
                        </div>
                        <h3 className="font-semibold text-lg">
                          {market.symbol}
                        </h3>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {displayName}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <div className="text-muted-foreground">
                          Min Order Value
                        </div>
                        <div className="font-semibold">
                          ${parseFloat(market.min_order_value_usd).toFixed(2)} USD
                        </div>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <div className="text-muted-foreground">
                          Tick Size
                        </div>
                        <div className="font-semibold">
                          ${market.tick_size}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Other markets in a compact grid */}
              {otherMarkets.length > 0 && (
                <>
                  {featuredMarkets.length > 0 && (
                    <h3 className="font-semibold text-lg mt-8 mb-4">
                      All Markets
                    </h3>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {otherMarkets.map((market) => {
                      const displayName =
                        market.display_name || market.base_symbol;
                      return (
                        <div
                          key={market.symbol}
                          className="p-4 border border-border rounded-lg bg-card"
                        >
                          <div className="font-semibold text-sm">
                            {market.symbol}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {displayName}
                          </div>
                          <div className="text-xs text-muted-foreground mt-2">
                            Min: ${parseFloat(market.min_order_value_usd).toFixed(2)} USD
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {/* Understanding Specs */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            Understanding Market Specifications
          </h2>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Minimum Order Value</h3>
              <p className="text-sm text-muted-foreground">
                The minimum USD notional value for an order in a market.
                Orders below this value will be rejected.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Tick Size</h3>
              <p className="text-sm text-muted-foreground">
                The minimum price increment for the market. Prices must be
                multiples of the tick size.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Maximum Leverage</h3>
              <p className="text-sm text-muted-foreground">
                Markets support leverage up to{" "}
                <strong className="text-foreground">100x</strong>. Higher
                leverage is extremely risky and not recommended for most
                traders. Always consider your risk tolerance before using
                high leverage.
              </p>
            </div>
          </div>
        </section>

        {/* Price Data */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            Price Data Sources
          </h2>

          <p className="text-muted-foreground mb-4">
            Nordex uses real-time price feeds from professional oracle
            providers via WebSocket connections to ensure accurate,
            up-to-date market prices:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Live Prices:</strong>{" "}
              Market prices update in real-time from multiple aggregated
              data sources
            </li>
            <li>
              <strong className="text-foreground">Order Book:</strong> Live
              order book depth from major exchanges
            </li>
            <li>
              <strong className="text-foreground">Market Trades:</strong>{" "}
              Recent trade history from aggregated sources
            </li>
            <li>
              <strong className="text-foreground">
                24h Statistics:
              </strong>{" "}
              Volume, price change, and other 24-hour market statistics
            </li>
          </ul>
        </section>

        {/* Switching Markets */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            Switching Markets
          </h2>

          <p className="text-muted-foreground mb-4">
            You can easily switch between markets using the market selector
            in the header:
          </p>

          <div className="space-y-3">
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm">
                <strong>1.</strong> Click the market name in the header
                (e.g., "BTC-USD")
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm">
                <strong>2.</strong> A dropdown will appear showing all
                available markets with current prices
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm">
                <strong>3.</strong> Click the market you want to trade
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm">
                <strong>4.</strong> All panels (chart, order book, order
                form, etc.) will update to show the selected market
              </p>
            </div>
          </div>
        </section>

        <DocsNavigation
          previous={{
            title: "Position Management",
            href: "/position-management",
          }}
          next={{ title: "Fee Structure", href: "/fees" }}
        />
      </div>
    </DocsLayout>
  );
};
